import { useId, useState } from 'react'
import type { KbSyncFile } from '@/lib/api/endpoints/ai-research'

import { MAX_UPLOAD_BYTES } from '@/lib/api/endpoints/documents'
import { cx } from '@/lib/browser/class-names'
import { ACCEPT_ATTRIBUTE, formatBytes } from '@/lib/documents/accepted-types'

import { SETTLED, useUploadQueue, type UploadItem } from './use-upload'

export interface UploadZoneProps {
  onUploaded?:
    | ((input: { companyId: string; files: readonly KbSyncFile[]; uploaded: number }) => void)
    | undefined

  companyId: string | undefined
  companies: readonly { readonly id: string; readonly name: string }[]
}

const PHASE_LABEL: Record<UploadItem['phase'], string> = {
  queued: 'Queued',
  rejected: 'Not uploaded',
  reserving: 'Preparing…',
  uploading: 'Uploading',
  verifying: 'Verifying…',
  done: 'Uploaded',
  failed: 'Failed',
}

const FIELD_LABEL: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: 'var(--text-2)',
  display: 'block',
}

export function UploadZone({ companyId, companies, onUploaded }: UploadZoneProps) {
  const { items, isUploading, start, clearFinished } = useUploadQueue()
  const [dragOver, setDragOver] = useState(false)
  const [notes, setNotes] = useState('')

  const [uploadCompany, setUploadCompany] = useState('')
  const target = uploadCompany === '' ? (companyId ?? '') : uploadCompany

  const [noCompany, setNoCompany] = useState(false)

  const companyFieldId = useId()
  const notesFieldId = useId()

  function submit(files: FileList | null) {
    if (!files || files.length === 0) return

    if (target === '') {
      setNoCompany(true)
      return
    }

    setNoCompany(false)
    void start({
      companyId: target,
      files: [...files],
      periodCovered: notes.trim() === '' ? undefined : notes.trim(),
    }).then(({ uploaded, indexed }) => {
      if (uploaded > 0) onUploaded?.({ companyId: target, files: indexed, uploaded })
    })
  }

  const finished = items.filter((item) => SETTLED.has(item.phase)).length

  return (
    <div className="canopy-card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>Upload files</div>
        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
          Board materials, AI inventories, product decks, diligence docs
        </div>
      </div>

      <div style={{ padding: 18 }}>
        <div
          className={cx('canopy-upload-zone', dragOver && 'drag-over')}
          onDragOver={(event) => {
            event.preventDefault()
            if (!isUploading) setDragOver(true)
          }}
          onDragLeave={() => {
            setDragOver(false)
          }}
          onDrop={(event) => {
            event.preventDefault()
            setDragOver(false)
            if (!isUploading) submit(event.dataTransfer.files)
          }}
        >
          <input
            type="file"
            multiple
            accept={ACCEPT_ATTRIBUTE}
            aria-label="Upload documents"
            disabled={isUploading}
            onChange={(event) => {
              submit(event.target.files)
              event.target.value = ''
            }}
          />
          <div className="canopy-upload-icon">
            <svg
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              aria-hidden="true"
            >
              <path
                d="M10 13V7M10 7L7.5 9.5M10 7L12.5 9.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3 14v1.5A1.5 1.5 0 004.5 17h11a1.5 1.5 0 001.5-1.5V14"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div className="canopy-upload-title">Drop files here or click to browse</div>
          <div className="canopy-upload-sub">
            PDF · Excel · CSV · PowerPoint · text · markdown · images — up to{' '}
            {formatBytes(MAX_UPLOAD_BYTES)} each
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
          <div>
            <label htmlFor={companyFieldId} style={{ ...FIELD_LABEL, marginBottom: 4 }}>
              Associate with company
            </label>
            <select
              id={companyFieldId}
              className="canopy-select-ctrl"
              style={{ width: '100%' }}
              value={target}
              disabled={isUploading}
              onChange={(event) => {
                setUploadCompany(event.target.value)
                setNoCompany(false)
              }}
            >
              <option value="">— Select company —</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
              <label htmlFor={notesFieldId} style={FIELD_LABEL}>
                Context notes
              </label>
              <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-3)' }}>
                (optional)
              </span>
            </div>
            <input
              id={notesFieldId}
              type="text"
              value={notes}
              disabled={isUploading}
              placeholder="e.g. March 2026 board materials, Q1 AI inventory…"
              onChange={(event) => {
                setNotes(event.target.value)
              }}
              style={{
                width: '100%',
                fontFamily: 'var(--font)',
                fontSize: 12,
                padding: '6px 10px',
                height: 32,
                border: '1px solid var(--border-strong)',
                borderRadius: 'var(--r-md)',
                background: 'var(--surface)',
                color: 'var(--text)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        {noCompany && (
          <div role="alert" style={{ marginTop: 10, fontSize: 11, color: 'var(--red-600)' }}>
            Pick a company before uploading. Use the sidebar or the “Associate with company”
            dropdown.
          </div>
        )}

        {items.length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {items.map((item) => (
              <QueueRow key={item.id} item={item} />
            ))}
            {finished > 0 && !isUploading && (
              <div>
                <button
                  type="button"
                  className="canopy-btn"
                  style={{ fontSize: 11, padding: '5px 12px' }}
                  onClick={clearFinished}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const PHASE_PILL: Record<UploadItem['phase'], { background: string; color: string }> = {
  queued: { background: 'var(--gray-100)', color: 'var(--text-3)' },
  reserving: { background: 'var(--gray-100)', color: 'var(--text-3)' },
  uploading: { background: 'var(--amber-50)', color: 'var(--amber-800)' },
  verifying: { background: 'var(--amber-50)', color: 'var(--amber-800)' },
  done: { background: 'var(--green-50)', color: 'var(--green-800)' },
  rejected: { background: 'var(--amber-50)', color: 'var(--amber-800)' },
  failed: { background: 'var(--red-50)', color: 'var(--red-600)' },
}

function QueueRow({ item }: { item: UploadItem }) {
  const showBar = item.phase === 'uploading' || item.phase === 'verifying'

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'var(--text-2)',
          }}
        >
          {item.fileName}
        </span>
        <span style={{ color: 'var(--text-3)' }}>{formatBytes(item.sizeBytes)}</span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 500,
            padding: '1px 7px',
            borderRadius: 4,
            whiteSpace: 'nowrap',
            ...PHASE_PILL[item.phase],
          }}
        >
          {PHASE_LABEL[item.phase]}
          {item.phase === 'uploading' && ` ${String(Math.round(item.progress * 100))}%`}
        </span>
      </div>
      {showBar && (
        <div className="canopy-upload-progress" style={{ display: 'block' }}>
          <div
            className="canopy-upload-progress-bar"
            style={{ width: `${String(Math.round(item.progress * 100))}%` }}
          />
        </div>
      )}
      {item.error !== null && (
        <div role="alert" style={{ fontSize: 11, color: 'var(--red-600)', marginTop: 2 }}>
          {item.error}
        </div>
      )}
    </div>
  )
}
